import React, { useState } from 'react';
import axios from 'axios';
import { Search, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import { API_BASE } from '../config';

// const API_BASE = "http://127.0.0.1:8000";

export default function SearchBar({ setResults, setDebugInfo }) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [topK, setTopK] = useState(10);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setResults([]);
    // Provide a callback if passed, to clear previous debug info
    if (typeof setDebugInfo === 'function') setDebugInfo(null);

    const usedTopK = topK; // Capture current state
    try {
      const response = await axios.get(`${API_BASE}/nl_search`, {
        params: {
          query: query,
          top_k: usedTopK,
          enable_fallback: false // Strict mode for Discover search
        }
      });
      const data = response.data;

      // Handle new response format { results: [], debug_parsed_query: {} }
      if (data.results && Array.isArray(data.results)) {
        setResults(data.results);
        if (data.results.length === 0) toast.success("Searching complete, but no matches found.", { icon: '🔍' });

        // Pass debug info up
        if (typeof setDebugInfo === 'function' && data.debug_parsed_query) {
          setDebugInfo(data.debug_parsed_query);
        }

      } else if (Array.isArray(data)) {
        // Fallback for legacy array response
        setResults(data);
        if (data.length === 0) toast.success("Searching complete, but no matches found.", { icon: '🔍' });
      } else {
        setResults([]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full space-y-4">
      <div className="relative group">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <Search className={clsx("h-5 w-5 transition-colors", loading ? "text-brand-500" : "text-slate-400 group-focus-within:text-brand-600 dark:text-slate-500 dark:group-focus-within:text-brand-400")} />
        </div>
        <input
          type="text"
          className="block w-full pl-11 pr-4 py-4 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all shadow-sm group-hover:shadow-md dark:bg-slate-800 dark:border-slate-700 dark:text-white dark:placeholder-slate-500 dark:focus:ring-brand-500/40"
          placeholder="Describe your ideal candidate (e.g., 'React developer with 5 years experience')"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          disabled={loading}
        />
        <div className="absolute inset-y-0 right-2 flex items-center gap-2">
          <select
            value={topK}
            onChange={(e) => setTopK(Number(e.target.value))}
            className="h-9 px-2 bg-slate-100 text-slate-600 text-xs rounded-lg border-none focus:ring-2 focus:ring-brand-500/20 outline-none cursor-pointer dark:bg-slate-700 dark:text-slate-300"
            title="Max Results"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
          <button
            onClick={handleSearch}
            disabled={loading || !query.trim()}
            className="bg-brand-600 hover:bg-brand-700 text-white p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <span className="text-sm font-semibold px-2">Search</span>}
          </button>
        </div>
      </div>

      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {['all', 'experienced', 'manager', 'python', 'javascript'].map((filter) => (
          <button
            key={filter}
            onClick={() => {
              setActiveFilter(filter);
              if (filter !== 'all') setQuery(q => q ? `${q} ${filter}` : filter);
            }}
            className={clsx(
              "px-3 py-1.5 rounded-full text-xs font-medium border transition-all whitespace-nowrap",
              activeFilter === filter
                ? "bg-slate-800 text-white border-slate-800 dark:bg-brand-600 dark:border-brand-600"
                : "bg-white text-slate-600 border-slate-200 hover:border-brand-300 hover:text-brand-600 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700 dark:hover:border-slate-600 dark:hover:text-brand-400"
            )}
          >
            {filter.charAt(0).toUpperCase() + filter.slice(1)}
          </button>
        ))}
      </div>
    </div>
  );
}
